import { AddTask, ArrowDropDown, Save, Visibility } from '@mui/icons-material';
import {
  Button,
  ButtonGroup,
  type ButtonProps,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { InfoTooltip } from '@/Components/ui/Tooltip';
import type { RouterType } from '@/Router';
import type { SetStateFn } from '@/Types';
import { useUserPostCaps } from '@/Utility/auth/Auth';
import { useUser } from '@/Utility/hooks';
import type { Frags_Status_Settings_Fragment$data } from '@/graphql/queries/__generated__/Frags_Status_Settings_Fragment.graphql';
import { PostStatusEnum, type StatusSettings } from '@/graphql/types';
import { type ToOptions, createLink } from '@tanstack/react-router';
import { format as dateFormat } from 'date-fns';
import { toast } from 'react-toastify';
import type { DeepNullable, DeepPartial } from 'ts-essentials';

import {
  type NodeTypesForStatus,
  STATUS_GROUPS,
  type StatusGroups,
  type UseStatusProps,
  getIsStatus,
  getStatusGroupsFromSettings,
  getStatusSettingsFromGroups,
  getStatusTypeObject,
  useStatusesAllowed,
} from './utils';

import styles from '../status/status-dropdown.module.css';

const MuiButtonLink = createLink(Button);

const initStatusDropdownContext = {
  radioAdd: STATUS_GROUPS.addAs[PostStatusEnum.Draft].value as StatusGroups['addAs']['value'],
  setRadioAdd: (() => {}) as SetStateFn<StatusGroups['addAs']['value']>,
  radioVisibility: STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value as NonNullable<
    StatusGroups['visibility']
  >['value'],
  setRadioVisibility: (() => {}) as SetStateFn<NonNullable<StatusGroups['visibility']>['value']>,
} as const;

const StatusDropdownContext = createContext<typeof initStatusDropdownContext>({ ...initStatusDropdownContext });

const useStatusDropdownContext = (): typeof initStatusDropdownContext => {
  return useContext(StatusDropdownContext);
};

type GenericStatusSettings =
  | StatusSettings
  | DeepNullable<DeepPartial<Frags_Status_Settings_Fragment$data>>
  | null
  | undefined;

type StatusDropdownComponentProps<T extends keyof NodeTypesForStatus> = {
  /**
   * The input for the status dropdown. Separate from the statusFromDb
   */
  input: GenericStatusSettings;
  /**
   * The original status from the db, this ensures that view can be disabled if the status is draft, pending or trash
   * We can also determine the actual current state of the status before saving
   */
  statusFromDb: GenericStatusSettings;
  usePermissions?: boolean;
  onChange: (data: StatusSettings) => void;
  disabled?: boolean;
  isFallback?: boolean;
  className?: string;
} & UseStatusProps<T>;

const StatusDropdownComponentDefault = <T extends keyof NodeTypesForStatus>({
  input,
  statusFromDb,
  usePermissions,
  onChange,
  className,
  disabled,
  type,
  statusOptions,
  isFallback,
}: StatusDropdownComponentProps<T>) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'status-dropdown',
  });
  const allowedStatuses = useStatusesAllowed(type, statusOptions);

  const { radioAdd, setRadioAdd, radioVisibility, setRadioVisibility } = useStatusDropdownContext();

  const isRadioDisabled = useCallback(
    (
      type: 'add-as' | 'visibility',
      options: Array<
        NonNullable<StatusGroups['visibility']>['value'] | StatusGroups['addAs']['value'] //
      >
    ) => {
      if (type === 'visibility') {
        if (radioAdd === PostStatusEnum.Pending) {
          return false;
        }

        return !options.some((s) => allowedStatuses.find(({ value }) => value === s));
      }
    },
    [radioAdd, allowedStatuses]
  );

  useEffect(() => {
    if (statusFromDb) {
      const status = getStatusGroupsFromSettings(statusFromDb as StatusSettings);
      if (status) {
        if (status.addAs.value !== radioAdd) {
          setRadioAdd(status.addAs.value);
        }
        if (status.visibility?.value !== radioVisibility) {
          setRadioVisibility(status.visibility?.value ?? STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value);
        }
      }
    }
  }, [statusFromDb]);

  const user = useUser();
  const userPostCaps = useUserPostCaps(type);

  const canCurrentUserApproveThisPost = useMemo(() => {
    if (!statusOptions?.author?.id || statusOptions?.author?.id === user?.id) {
      return false;
    }

    // current user needs to have the following permissions
    if (
      userPostCaps.publish_posts === false ||
      userPostCaps.publish_hidden_post === false ||
      userPostCaps.edit_others_posts === false
    ) {
      return false;
    }

    // current status needs to be pending
    if (getIsStatus(statusFromDb?.status, [PostStatusEnum.Pending]) === false) {
      return false;
    }

    return true;
  }, [statusOptions, userPostCaps, user?.id, statusFromDb]);

  const isThisPostApproved = useMemo(() => {
    const ret = {
      value: false,
      approvedByCurrentUser: false,
      timestamp: null as string | null,
      date: null as string | null,
    };

    if (getIsStatus(input?.status, [PostStatusEnum.Pending])) {
      return ret;
    }

    if (!!input?.approved?.id) {
      ret.value = true;

      if (
        (statusFromDb?.approved?.id === user?.id && statusFromDb?.approved?.id !== null) ||
        (input?.approved?.id === user?.id && input?.approved?.id !== null)
      ) {
        ret.approvedByCurrentUser = true;
      }

      ret.timestamp = statusFromDb?.approved?.timestamp ?? input?.approved?.timestamp ?? null;

      if (statusFromDb?.approved?.timestamp) {
        // save the date in the format of Fri 1st of jan 2021
        ret.date = dateFormat(new Date(statusFromDb.approved.timestamp ?? input?.approved?.timestamp), 'EEE do MMM yy');
      }
    }

    return ret;
  }, [input, statusFromDb?.approved, user?.id]);

  const buttonContent = useMemo(() => {
    if (isFallback) {
      return <LinearProgress color="inherit" sx={{ width: 100 }} />;
    }

    const settings = getStatusSettingsFromGroups(radioAdd, radioVisibility);
    const groups = getStatusGroupsFromSettings(settings!);

    const visibilityIcon = !!groups?.visibility ? (
      <>
        {' | '} {groups?.visibility?.icon}
      </>
    ) : (
      <></>
    );

    if (canCurrentUserApproveThisPost) {
      return (
        <>
          <AddTask />
          <Typography variant="button">Requires Approval</Typography>
          {visibilityIcon}
        </>
      );
    }

    const statusObject = getStatusTypeObject(settings);
    return (
      <>
        {groups?.addAs?.icon}
        <Typography variant="button">{isThisPostApproved.value ? 'Approved' : statusObject?.label}</Typography>
        {visibilityIcon}
      </>
    );
  }, [canCurrentUserApproveThisPost, isThisPostApproved, isFallback, radioAdd, radioVisibility]);

  return (
    <>
      <Tooltip title={`${STATUS_GROUPS.addAs[radioAdd].label} -> ${STATUS_GROUPS.visibility[radioVisibility].label}`}>
        <span>
          <Button
            className={`${styles.dropdownButton} ${className} ${canCurrentUserApproveThisPost && styles.forApproval} ${isThisPostApproved.value && styles.approved}`}
            {...bindTrigger(popupState)}
            disabled={disabled || allowedStatuses?.length === 0 || isFallback}
          >
            {buttonContent}
            <ArrowDropDown
              sx={{
                transform: `rotate(${popupState.isOpen ? '180deg' : '0deg'})`,
                transition: 'transform 0.3s cubic-bezier(0.65, 0.05, 0.36, 1)',
              }}
            />
          </Button>
        </span>
      </Tooltip>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              borderRadius: 1,
              boxShadow: 6,
              minWidth: 250,
            },
          },
        }}
      >
        <Stack spacing={2}>
          {isThisPostApproved.value && (
            <div>
              <Typography variant="body2" color="text.secondary" sx={{ cursor: 'default' }}>
                {isThisPostApproved.approvedByCurrentUser ? 'You' : (statusFromDb?.approved?.user?.name ?? 'Someone')}{' '}
                approved this post{' '}
                {isThisPostApproved.date && (
                  <Tooltip title={dateFormat(new Date(isThisPostApproved?.timestamp ?? ''), 'PPpp')}>
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      {isThisPostApproved.date}
                    </Typography>
                  </Tooltip>
                )}
              </Typography>
            </div>
          )}

          <FormControl
            sx={{
              // hide the add as approved if the current user can approve this post
              // instead we show an approval button
              ...(canCurrentUserApproveThisPost && { display: 'none' }),
            }}
          >
            <FormLabel id="status-dropdown-add-as" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Add As
            </FormLabel>
            <RadioGroup
              name="add-as"
              value={radioAdd}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value as StatusGroups['addAs']['value'];
                setRadioAdd(value);

                onChange({ ...getStatusSettingsFromGroups(value, radioVisibility)!, approved: null });
              }}
            >
              <div>
                <FormControlLabel
                  value={STATUS_GROUPS.addAs[PostStatusEnum.Draft].value}
                  control={<Radio />}
                  label={STATUS_GROUPS.addAs[PostStatusEnum.Draft].label}
                  slotProps={{
                    typography: {
                      color: 'text.secondary',
                    },
                  }}
                  sx={{
                    ...(statusOptions?.exclude?.includes(PostStatusEnum.Draft) && { display: 'none' }),
                  }}
                />
                <InfoTooltip title={STATUS_GROUPS.addAs[PostStatusEnum.Draft].tooltip(type)} />
              </div>

              <div>
                <FormControlLabel
                  value={STATUS_GROUPS.addAs[PostStatusEnum.Pending].value}
                  control={<Radio />}
                  label={STATUS_GROUPS.addAs[PostStatusEnum.Pending].label}
                  slotProps={{
                    typography: {
                      color: 'text.secondary',
                    },
                  }}
                  sx={{
                    ...(statusOptions?.exclude?.includes(PostStatusEnum.Pending) && { display: 'none' }),
                  }}
                />
                <InfoTooltip title={STATUS_GROUPS.addAs[PostStatusEnum.Pending].tooltip(type)} />
              </div>

              <div>
                <FormControlLabel
                  value={STATUS_GROUPS.addAs.add_as_approved.value}
                  control={<Radio />}
                  label={STATUS_GROUPS.addAs.add_as_approved.label}
                  slotProps={{
                    typography: {
                      color: 'text.secondary',
                    },
                  }}
                  disabled={
                    [PostStatusEnum.Publish, PostStatusEnum.Hidden].some((s) =>
                      allowedStatuses.find(({ value }) => value === s)
                    ) === false
                  }
                />
                <InfoTooltip
                  title={STATUS_GROUPS.addAs.add_as_approved.tooltip(type)}
                  disabled={
                    [PostStatusEnum.Publish, PostStatusEnum.Hidden].some((s) =>
                      allowedStatuses.find(({ value }) => value === s)
                    ) === false
                  }
                />
              </div>
            </RadioGroup>
          </FormControl>

          <FormControl>
            <FormLabel id="status-dropdown-visibility" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Visibility
            </FormLabel>
            <RadioGroup
              name="visibility"
              value={radioVisibility}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value as NonNullable<StatusGroups['visibility']>['value'];
                setRadioVisibility(value);

                onChange(getStatusSettingsFromGroups(radioAdd, value)!);
              }}
            >
              {!statusOptions?.exclude?.includes(PostStatusEnum.Publish) && (
                <div>
                  <FormControlLabel
                    value={STATUS_GROUPS.visibility[PostStatusEnum.Publish].value}
                    control={<Radio />}
                    label={STATUS_GROUPS.visibility[PostStatusEnum.Publish].label}
                    slotProps={{
                      typography: {
                        color: 'text.secondary',
                      },
                    }}
                    disabled={isRadioDisabled('visibility', [PostStatusEnum.Publish])}
                  />
                  <InfoTooltip title={STATUS_GROUPS.visibility[PostStatusEnum.Publish].tooltip(type)} />
                </div>
              )}

              {!statusOptions?.exclude?.includes(PostStatusEnum.HiddenBS) && (
                <div>
                  <FormControlLabel
                    value={STATUS_GROUPS.visibility[PostStatusEnum.HiddenBS].value}
                    control={<Radio />}
                    label={STATUS_GROUPS.visibility[PostStatusEnum.HiddenBS].label}
                    slotProps={{
                      typography: {
                        color: 'text.secondary',
                      },
                    }}
                    disabled={isRadioDisabled('visibility', [PostStatusEnum.HiddenBS])}
                  />

                  <InfoTooltip title={STATUS_GROUPS.visibility[PostStatusEnum.HiddenBS].tooltip(type)} />
                </div>
              )}

              {!statusOptions?.exclude?.includes(PostStatusEnum.Hidden) && (
                <div>
                  <FormControlLabel
                    value={STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value}
                    control={<Radio />}
                    label={STATUS_GROUPS.visibility[PostStatusEnum.Hidden].label}
                    slotProps={{
                      typography: {
                        color: 'text.secondary',
                      },
                    }}
                    disabled={isRadioDisabled('visibility', [PostStatusEnum.Hidden])}
                  />
                  <InfoTooltip title={STATUS_GROUPS.visibility[PostStatusEnum.Hidden].tooltip(type)} />
                </div>
              )}

              {/* both private_restricted and hidden are excluded */}
              {usePermissions &&
                !(
                  statusOptions?.exclude?.includes(PostStatusEnum.Hidden) ||
                  statusOptions?.exclude?.includes('private_restricted')
                ) && (
                  <div>
                    <FormControlLabel
                      value={STATUS_GROUPS.visibility.private_restricted.value}
                      control={<Radio />}
                      label={STATUS_GROUPS.visibility.private_restricted.label}
                      slotProps={{
                        typography: {
                          color: 'text.secondary',
                        },
                      }}
                      disabled={isRadioDisabled('visibility', [PostStatusEnum.Hidden])}
                    />
                    <InfoTooltip title={STATUS_GROUPS.visibility.private_restricted.tooltip(type)} />
                  </div>
                )}
            </RadioGroup>
          </FormControl>

          {canCurrentUserApproveThisPost && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => {
                const value = STATUS_GROUPS.addAs.add_as_approved.value;
                setRadioAdd(value);

                onChange({
                  ...getStatusSettingsFromGroups(value, radioVisibility)!,
                  approved: {
                    id: user?.id,
                  },
                });

                popupState.close();
                toast.success('Changes will be applied on save');
              }}
            >
              Approve
            </Button>
          )}
        </Stack>
      </Popover>
    </>
  );
};

type StatusDropdownProps<T extends keyof NodeTypesForStatus> = {
  /**
   * Slot props for the status dropdown
   */
  slotProps?: {
    /**
     * View link button - on the left
     */
    view?: {
      link: string | ToOptions<RouterType>;
      text?: string;
      disabled?: boolean;
    };

    /**
     * Save button - on the right
     */
    save?: {
      text?: string;
      onClick?: () => void;
      disabled?: boolean;
    };
    buttonLeft?: {
      text?: string;
      button: ButtonProps;
    };
    buttonRight?: {
      text?: string;
      button: ButtonProps;
    };
  };
} & StatusDropdownComponentProps<T>;

const WithView = <T extends keyof NodeTypesForStatus>(
  props: NonNullable<NonNullable<StatusDropdownProps<T>['slotProps']>['view']> & {
    statusFromDb: GenericStatusSettings;
    type: T;
  }
) => {
  const canViewPost = useMemo(() => {
    if (!props?.statusFromDb || props?.disabled) {
      return false;
    }

    const statusFromDb = props.statusFromDb;
    // given the status from the db, we can determine if the user can view the post,
    // we cannot view if the status is draft, pending or trash
    if (getIsStatus(statusFromDb.status, [PostStatusEnum.Draft, PostStatusEnum.Pending, PostStatusEnum.Trash])) {
      return false;
    }

    return true;
  }, [props]);

  if (typeof props?.link === 'object') {
    return (
      <MuiButtonLink
        className={`${styles.button} ${styles.rightButton}`}
        {...props.link}
        target="_blank"
        startIcon={<Visibility />}
        disabled={canViewPost === false}
      >
        <Typography>{props.text ?? 'View'}</Typography>
      </MuiButtonLink>
    );
  }

  return (
    <Button
      component="a"
      className={`${styles.button} ${styles.rightButton}`}
      href={props.link}
      target="_blank"
      startIcon={<Visibility />}
      disabled={canViewPost === false}
    >
      <Typography>{props.text ?? 'View'}</Typography>
    </Button>
  );
};

const WithSave = <T extends keyof NodeTypesForStatus>(
  props: NonNullable<NonNullable<StatusDropdownProps<T>['slotProps']>['save']> & {
    statusFromDb: GenericStatusSettings;
    type: T;
    statusOptions?: UseStatusProps<T>['statusOptions'];
  }
) => {
  const { radioAdd } = useStatusDropdownContext();
  const userPostCaps = useUserPostCaps(props.type);
  const user = useUser();

  // if the status is in an approved state and the current user does not have the permission to publish posts
  // then we disable the save button
  const canSavePost = useMemo(() => {
    if (props?.disabled) {
      return false;
    }

    const postAuthor = props?.statusOptions?.author?.id;

    // if the current user is not the author of the post
    if (postAuthor !== null && postAuthor !== user?.id) {
      if (userPostCaps.edit_others_posts === false) {
        return false;
      }
    }

    // if the current status is approved,
    if (radioAdd === STATUS_GROUPS.addAs.add_as_approved.value) {
      if (userPostCaps.publish_posts === false && userPostCaps.publish_hidden_post === false) {
        return false;
      }
    }

    return true;
  }, [props, userPostCaps, user?.id, radioAdd]);

  return (
    <Button
      className={`${styles.button} ${styles.leftButton}`}
      onClick={props?.onClick}
      disabled={props?.disabled || canSavePost === false}
      startIcon={<Save />}
    >
      <Typography>{props.text ?? 'Save'}</Typography>
    </Button>
  );
};

/**
 * Status dropdown component
 * Based on the slot props, it can render a view button on the left and a save button on the right
 */
const StatusDropdownReducer = <T extends keyof NodeTypesForStatus>({ slotProps, ...props }: StatusDropdownProps<T>) => {
  if (!!slotProps && (!!slotProps?.view || !!slotProps?.save || !!slotProps?.buttonLeft || !!slotProps?.buttonRight)) {
    return (
      <ButtonGroup>
        {slotProps?.save && (
          <WithSave
            type={props.type}
            {...slotProps.save}
            statusFromDb={props?.statusFromDb}
            statusOptions={props?.statusOptions}
          />
        )}
        {!!slotProps?.buttonLeft && !slotProps?.view && (
          <Button
            {...slotProps.buttonLeft.button}
            className={`${styles.button} ${styles.leftButton} ${slotProps.buttonLeft?.button?.className}`}
          >
            <Typography>{slotProps.buttonLeft.text}</Typography>
          </Button>
        )}

        <StatusDropdown {...props} className={!!slotProps?.view || slotProps?.buttonRight ? styles.centerButton : ''} />

        {slotProps?.view && <WithView type={props.type} {...slotProps.view} statusFromDb={props?.statusFromDb} />}
        {slotProps?.buttonRight && !slotProps?.save && (
          <Button
            {...slotProps.buttonRight.button}
            className={`${styles.button} ${styles.rightButton} ${slotProps.buttonRight?.button?.className}`}
          >
            <Typography>{slotProps.buttonRight.text}</Typography>
          </Button>
        )}
      </ButtonGroup>
    );
  }

  return <StatusDropdownComponentDefault {...props} />;
};

export const StatusDropdown = <T extends keyof NodeTypesForStatus>(props: StatusDropdownProps<T>) => {
  const [radioAdd, setRadioAdd] = useState<StatusGroups['addAs']['value']>(
    STATUS_GROUPS.addAs[PostStatusEnum.Draft].value
  );

  const [radioVisibility, setRadioVisibility] = useState<NonNullable<StatusGroups['visibility']>['value']>(
    STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value
  );

  useEffect(() => {
    if (props.input) {
      const status = getStatusGroupsFromSettings(props.input as StatusSettings);
      if (status) {
        if (status.addAs.value !== radioAdd) {
          setRadioAdd(status.addAs.value);
        }
        if (status.visibility?.value !== radioVisibility) {
          setRadioVisibility(status.visibility?.value ?? STATUS_GROUPS.visibility[PostStatusEnum.Hidden].value);
        }
      }
    }
  }, [props.input]);

  return (
    <StatusDropdownContext.Provider value={{ radioAdd, setRadioAdd, radioVisibility, setRadioVisibility }}>
      <StatusDropdownReducer {...props} />
    </StatusDropdownContext.Provider>
  );
};
